// ════════════════════════════════════════════════════════════════════════════
// 💳 SplitPaymentModal — Phase 2.1 N-way split across payment methods
// ════════════════════════════════════════════════════════════════════════════
// Lets user allocate the grand total across cash / card / mobile in any mix.
// Wires to existing backend `splitDetails` JSON field on /api/pos-checkout.
// On confirm: calls onConfirm({ methods, splitDetails }) — parent dispatches.
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Plus, Trash2, Banknote, CreditCard, Smartphone, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Haptics } from '../../utils/haptics';

type Method = 'cash' | 'card' | 'mobile';

interface Leg {
  id: string;
  method: Method;
  amount: string;
  provider?: string; // mobile only
}

interface SplitPaymentModalProps {
  open: boolean;
  grandTotal: number;
  onClose: () => void;
  onConfirm: (data: { legs: Leg[]; splitDetails: any }) => void;
}

const formatRs = (n: number) =>
  'Rs. ' + n.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MOBILE_PROVIDERS = ['FriMoney', 'PayHere', 'Dialog goPay', 'HNB SOLO', 'BOC mPay', 'ezCash'];

const newLeg = (method: Method): Leg => ({
  id: Math.random().toString(36).slice(2, 9),
  method,
  amount: '',
});

const SplitPaymentModal: React.FC<SplitPaymentModalProps> = ({ open, grandTotal, onClose, onConfirm }) => {
  const [legs, setLegs] = useState<Leg[]>([]);

  useEffect(() => {
    if (open) {
      // Seed with a 50/50 cash+card suggestion
      const half = +(grandTotal / 2).toFixed(2);
      const other = +(grandTotal - half).toFixed(2);
      setLegs([
        { ...newLeg('cash'), amount: half.toString() },
        { ...newLeg('card'), amount: other.toString() },
      ]);
    }
  }, [open, grandTotal]);

  const totalAllocated = useMemo(
    () => legs.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0),
    [legs]
  );
  const remaining = +(grandTotal - totalAllocated).toFixed(2);
  const isBalanced = Math.abs(remaining) < 0.01;
  const overPaid = remaining < -0.01;

  const updateLeg = (id: string, patch: Partial<Leg>) =>
    setLegs((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));

  const addLeg = (method: Method) => {
    Haptics.fire('tap');
    // Auto-fill remaining into the new leg if positive
    const fill = remaining > 0 ? remaining.toString() : '';
    setLegs((prev) => [...prev, { ...newLeg(method), amount: fill }]);
  };

  const removeLeg = (id: string) => {
    Haptics.fire('warn');
    setLegs((prev) => prev.filter((l) => l.id !== id));
  };

  const distributeRemaining = (id: string) => {
    if (remaining <= 0) return;
    Haptics.fire('tap');
    updateLeg(id, {
      amount: ((parseFloat(legs.find((l) => l.id === id)?.amount || '0') || 0) + remaining).toFixed(2),
    });
  };

  const confirm = () => {
    if (!isBalanced) return;
    // Validate mobile legs have a provider
    const bad = legs.find((l) => l.method === 'mobile' && !l.provider);
    if (bad) return;
    Haptics.fire('success');

    // Determine primary paymentMethod = leg with largest amount
    const legsNum = legs.map((l) => ({ ...l, n: parseFloat(l.amount) || 0 }));
    const primary = legsNum.reduce((a, b) => (b.n > a.n ? b : a));

    const splitDetails = {
      legs: legsNum.map((l) => ({
        method: l.method,
        amount: +l.n.toFixed(2),
        provider: l.provider || null,
      })),
      totalPaid: +totalAllocated.toFixed(2),
      grandTotal: +grandTotal.toFixed(2),
    };

    onConfirm({
      legs,
      splitDetails: { ...splitDetails, primaryMethod: primary.method },
    });
  };

  if (!open) return null;

  const methodIcon = (m: Method) => {
    if (m === 'cash') return <Banknote size={13} className="text-emerald-600" />;
    if (m === 'card') return <CreditCard size={13} className="text-blue-600" />;
    return <Smartphone size={13} className="text-violet-600" />;
  };

  const methodRing = (m: Method) => {
    if (m === 'cash') return 'border-emerald-200 bg-emerald-50';
    if (m === 'card') return 'border-blue-200 bg-blue-50';
    return 'border-violet-200 bg-violet-50';
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="sp-bg"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10001] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          key="sp-card"
          initial={{ y: 400, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 400, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          className="w-full sm:max-w-lg max-h-[92vh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="shrink-0 flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
            <div>
              <h3 className="text-[14px] font-black text-slate-800 leading-tight">Split Payment</h3>
              <p className="text-[10px] text-slate-500">Allocate across multiple methods</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors"
              aria-label="Close"
            >
              <X size={15} />
            </button>
          </div>

          {/* Total bar */}
          <div className="shrink-0 px-5 py-3 bg-gradient-to-r from-brand-surface to-white border-b border-slate-100 flex items-center justify-between">
            <div>
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Grand Total</p>
              <p className="text-[20px] font-black font-mono text-slate-800 tabular-nums">{formatRs(grandTotal)}</p>
            </div>
            <div className="text-right">
              <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500">
                {overPaid ? 'Overpaid' : isBalanced ? 'Balanced' : 'Remaining'}
              </p>
              <p className={`text-[18px] font-black font-mono tabular-nums ${
                overPaid ? 'text-rose-500' : isBalanced ? 'text-emerald-500' : 'text-amber-500'
              }`}>
                {overPaid ? '- ' : ''}{formatRs(Math.abs(remaining))}
              </p>
            </div>
          </div>

          {/* Legs */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
            {legs.map((leg, idx) => (
              <div key={leg.id} className={`rounded-2xl border p-3 ${methodRing(leg.method)}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {methodIcon(leg.method)}
                    <span className="text-[11px] font-black uppercase tracking-wider text-slate-700">
                      {leg.method} #{idx + 1}
                    </span>
                  </div>
                  {legs.length > 1 && (
                    <button
                      onClick={() => removeLeg(leg.id)}
                      className="w-7 h-7 rounded-lg hover:bg-white flex items-center justify-center text-rose-400 hover:text-rose-600 transition-colors"
                      aria-label="Remove leg"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-mono text-slate-500 shrink-0">Rs.</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={leg.amount}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9.]/g, '');
                      updateLeg(leg.id, { amount: v });
                    }}
                    placeholder="0.00"
                    className="flex-1 px-3 py-2 rounded-xl bg-white border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[14px] font-mono font-bold text-slate-800 text-right tabular-nums"
                  />
                  {remaining > 0 && (
                    <button
                      onClick={() => distributeRemaining(leg.id)}
                      className="px-2 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-brand-primary hover:text-brand-primary text-[9px] font-black uppercase tracking-wider text-slate-500 transition-colors shrink-0"
                    >
                      +{remaining.toFixed(0)}
                    </button>
                  )}
                </div>

                {leg.method === 'mobile' && (
                  <div className="mt-2 grid grid-cols-3 gap-1">
                    {MOBILE_PROVIDERS.map((p) => (
                      <button
                        key={p}
                        onClick={() => updateLeg(leg.id, { provider: p })}
                        className={`py-1.5 rounded-lg text-[9px] font-bold border transition-all ${
                          leg.provider === p
                            ? 'bg-violet-500 border-violet-500 text-white'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-violet-300'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Add-leg buttons */}
            <div className="grid grid-cols-3 gap-2 pt-1">
              {(['cash', 'card', 'mobile'] as Method[]).map((m) => (
                <button
                  key={m}
                  onClick={() => addLeg(m)}
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-slate-300 hover:border-brand-primary hover:bg-brand-surface text-slate-500 hover:text-brand-primary text-[10px] font-black uppercase tracking-wider transition-all"
                >
                  <Plus size={12} /> {m}
                </button>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 px-4 pt-2 pb-4 border-t border-slate-100 bg-white">
            {overPaid && (
              <div className="flex items-center gap-2 text-[10px] text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-1.5 mb-2">
                <AlertTriangle size={11} /> Allocated more than grand total. Adjust legs.
              </div>
            )}
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={confirm}
              disabled={!isBalanced || legs.some((l) => l.method === 'mobile' && !l.provider)}
              className="w-full py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-black text-[13px] uppercase tracking-widest shadow-lg shadow-emerald-500/25 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98]"
            >
              <CheckCircle2 size={14} /> Confirm Split · {formatRs(totalAllocated)}
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default SplitPaymentModal;
